import Address from "./Address";
import EnrichmentInfo, { EnrichmentInfoType } from "./EnrichmentInfo";

/***
 * id reserved for empty companies.
 */
const ID_COMPANY_EMPTY: number = -1;

export enum EnrichmentStatus {
  enrichmentAvailable = 0, // means that the company has not been enriched before and could maybe be enriched
  enriched = 1, // means that the company has been previously enriched
  enrichmentEmpty = 2 // means that an attempt was made to enrich the company and that no data was found
}

const normalizeOdooString = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? `${value}` : "";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return "";
    }
    if (trimmed.toLowerCase() === "false") {
      return "";
    }
    return trimmed;
  }
  return "";
};

const normalizeOdooNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === "false") {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

class Company {
  id: number;
  domain: string;
  name: string;
  website: string;
  image: string; // base64
  address: Address;
  phone: string;
  email: string;
  additionalInfo: {}; //Map<string, string>;

  enrichmentStatus: EnrichmentStatus;

  constructor() {
    this.id = ID_COMPANY_EMPTY;
    this.domain = "";
    this.name = "";
    this.website = "";
    this.phone = "";
    this.address = new Address();
    this.image = "";
    this.email = "";
    this.image = "";
    this.additionalInfo = {};
  }

  static fromJSON(o: Object, enrichmentInfo?: EnrichmentInfo): Company {
    if (!o) return new Company();
    const company = Object.assign(new Company(), o);
    const address = new Address();
    address.street = o["street"];
    address.city = o["city"];
    address.zip = o["zip"];
    // Handle country_id: can be array [id, name], false, or null
    address.country = (o["country_id"] && Array.isArray(o["country_id"]) && o["country_id"].length > 1) 
      ? o["country_id"][1] 
      : "";
    company.address = Address.fromJSON(address);
    // Handle image field: Odoo returns false when no image, convert to empty string
    // Also strip Python bytes literal notation (b'...' or b"...") if present
    if (o["image_1920"] && o["image_1920"] !== false) {
      let imageData = o["image_1920"];
      // Remove Python bytes literal notation if present (b'...' or b"...")
      if (typeof imageData === 'string') {
        // Match pattern b'...' or b"..."
        const bytesLiteralMatch = imageData.match(/^b(['"])(.*)\1$/);
        if (bytesLiteralMatch) {
          imageData = bytesLiteralMatch[2]; // Extract content between quotes
        }
      }
      company.image = imageData;
    } else {
      company.image = "";
    }

    if (enrichmentInfo != null) {
      switch (enrichmentInfo.type) {
        case EnrichmentInfoType.NoData:
          company.enrichmentStatus = EnrichmentStatus.enrichmentEmpty;
          break;
        case EnrichmentInfoType.CompanyCreated:
        case EnrichmentInfoType.CompanyUpdated:
          company.enrichmentStatus = EnrichmentStatus.enriched;
          break;
      }
    } else if (JSON.stringify(company.additionalInfo) != "{}") {
      company.enrichmentStatus = EnrichmentStatus.enriched;
    } else {
      company.enrichmentStatus = EnrichmentStatus.enrichmentAvailable;
    }

    return company;
  }

  static fromRevealJSON(o: Object): Company {
    const company = new Company();
    company.id = ID_COMPANY_EMPTY;
    company.additionalInfo = o;
    return company;
  }

  static getEmptyCompany = (): Company => {
    const company = new Company();
    company.id = ID_COMPANY_EMPTY;
    return company;
  };

  getDomain(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    let domain =
      normalizeOdooString(this.domain) ||
      normalizeOdooString(info["domain"]) ||
      normalizeOdooString(this.website);
    if (!domain) {
      return "";
    }
    if (domain && !domain.startsWith("http://") && !domain.startsWith("https://")) {
      domain = "https://" + domain;
    }
    return domain;
  }

  getBareDomain() {
    const info = this.additionalInfo as Record<string, unknown>;
    let domain = normalizeOdooString(this.domain) || normalizeOdooString(info["domain"]);
    if (domain) {
      domain = domain.replace(/(^\w+:|^)\/\//, "");
    }
    return domain;
  }

  getPhone(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    const phone = normalizeOdooString(this.phone);
    if (phone) {
      return phone;
    }
    const phoneNumbers = info["phone_numbers"];
    if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
      const candidateEntry = phoneNumbers.find(value => Boolean(normalizeOdooString(value)));
      const candidate = normalizeOdooString(candidateEntry ?? phoneNumbers[0]);
      if (candidate) {
        return candidate;
      }
    }
    return "";
  }

  getName(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(this.name) || normalizeOdooString(info["name"]);
  }

  getDescription(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["description"]);
  }

  getIndustry(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    const industries = [normalizeOdooString(info["industry_group"]), normalizeOdooString(info["sub_industry"])];
    return industries.filter(Boolean).join(", ");
  }

  getEmployees(): number | undefined {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooNumber(info["employees"]);
  }

  getYearFounded(): number | undefined {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooNumber(info["founded_year"]);
  }

  getKeywords(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    const tags = info["tag"];
    if (Array.isArray(tags)) {
      return tags.map(normalizeOdooString).filter(Boolean).join(", ");
    }
    return "";
  }

  getCompanyType(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["company_type"]);
  }

  getLogoURL(): string {
    return normalizeOdooString(this.image);
  }

  /* "annual_revenue" seems to be a number, often at 0.0 without a currency. Let's ignore this field.
        Data sample: "annual_revenue": 0.0,
                     "estimated_annual_revenue": "$50M-$100M",
    */
  getRevenue(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["estimated_annual_revenue"]);
  }

  getLocation(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    const addressLines = this.address.getLines().map(normalizeOdooString).filter(Boolean).join(", ");
    if (addressLines) {
      return addressLines;
    }
    return normalizeOdooString(info["location"]);
  }

  getTwitter(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["twitter"]);
  }

  getFacebook(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["facebook"]);
  }

  getLinkedin(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["linkedin"]);
  }

  getCrunchbase(): string {
    const info = this.additionalInfo as Record<string, unknown>;
    return normalizeOdooString(info["crunchbase"]);
  }

  getInitials(): string {
    const displayName = this.getName();
    if (!displayName) {
      return "";
    }
    const names = displayName.split(" ").filter(Boolean);
    if (!names.length) {
      return "";
    }
    let initials = names[0].substring(0, 1).toUpperCase();

    // If the company is more than two words, better only include the first letter of the first word.
    if (names.length == 2) {
      initials += names[1].substring(0, 1).toUpperCase();
    }

    return initials;
  }

  /***
   * Returns True if the company exists in the Odoo database, False otherwise
   */
  isAddedToDatabase(): boolean {
    return this.id && this.id > 0;
  }
}

export default Company;
